import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  MessageSquare, 
  Database, 
  CreditCard, 
  User, 
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  Star,
  Clock,
  Plus,
  Check,
  Factory,
  Building2,
  ShoppingCart,
  Cpu,
  Sparkles
} from 'lucide-react';

const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionsExpanded, setSessionsExpanded] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(2);
  const [starredSessionIds, setStarredSessionIds] = useState([10, 11, 12, 13, 14, 15, 16]); // IDs of starred sessions
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);
  const [showSessionsDropdown, setShowSessionsDropdown] = useState(false); // For collapsed state
  const [selectedWorkspace, setSelectedWorkspace] = useState('manufacturing');
  
  const sessionsDropdownRef = useRef(null);

  // Close sessions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sessionsDropdownRef.current && !sessionsDropdownRef.current.contains(event.target)) {
        setShowSessionsDropdown(false);
      }
    };
    
    if (showSessionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSessionsDropdown]);

  // Workspace data
  const workspaces = [
    { id: 'manufacturing', name: 'Manufacturing', icon: Factory, color: 'text-blue-400', bgColor: 'bg-blue-500/10', tables: 19 },
    { id: 'healthcare', name: 'Healthcare', icon: Building2, color: 'text-green-400', bgColor: 'bg-green-500/10', tables: 7 },
    { id: 'cpg', name: 'CPG', icon: ShoppingCart, color: 'text-purple-400', bgColor: 'bg-purple-500/10', tables: 0 },
    { id: 'smart-buildings', name: 'Smart Buildings', icon: Cpu, color: 'text-orange-400', bgColor: 'bg-orange-500/10', tables: 2 },
  ];

  const currentWorkspace = workspaces.find(w => w.id === selectedWorkspace) || workspaces[0];

  // Navigation items
  const navItems = [
    { 
      id: 'home', 
      label: 'Home', 
      icon: Home, 
      path: '/app/home',
      active: location.pathname === '/app/home'
    },
    { 
      id: 'connections', 
      label: 'Data Connections', 
      icon: Database, 
      path: '/app/connections',
      active: location.pathname === '/app/connections'
    },
    { 
      id: 'session', 
      label: 'Sessions', 
      icon: MessageSquare, 
      path: '/app/session',
      active: location.pathname === '/app/session' || location.pathname === '/dashboard',
      hasSubmenu: true
    },
  ];

  // Credits value (placeholder - would come from API)
  const creditsBalance = 1236;

  // Format credits for display
  const formatCredits = (num, abbreviated = false) => {
    if (abbreviated) {
      if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      return num.toString();
    }
    return num.toLocaleString();
  };

  // Mock sessions data (placeholder)
  const recentSessions = [
    { id: 1, name: 'Q4 Sales Analysis' },
    { id: 2, name: 'Customer Churn Prediction' },
    { id: 3, name: 'Marketing ROI Dashboard' },
  ];

  const starredSessions = [
    { id: 10, name: 'Annual Report 2024' },
    { id: 11, name: 'Board Presentation' },
    { id: 12, name: 'Inventory Optimization' },
    { id: 13, name: 'Revenue Forecasting' },
    { id: 14, name: 'Supply Chain Analytics' },
    { id: 15, name: 'Customer Segmentation' },
    { id: 16, name: 'Product Performance' },
  ];

  // Toggle star for a session
  const toggleStar = (sessionId, e) => {
    e.stopPropagation(); // Prevent session selection when clicking star
    setStarredSessionIds(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const handleNavClick = (item) => {
    if (item.hasSubmenu && isExpanded) {
      // Toggle submenu when expanded
      if (item.id === 'session') {
        setSessionsExpanded(!sessionsExpanded);
      }
    } else if (item.hasSubmenu && !isExpanded) {
      // Show dropdown when collapsed
      if (item.id === 'session') {
        setShowSessionsDropdown(!showSessionsDropdown);
      }
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="h-screen bg-slate-900 flex overflow-hidden">
      {/* Sidebar */}
      <div 
        className={`
          ${isExpanded ? 'w-60' : 'w-18'} 
          bg-slate-950 border-r border-slate-800/50 
          flex flex-col transition-all duration-300 ease-in-out
          flex-shrink-0 relative z-50
        `}
        style={{ width: isExpanded ? '240px' : '68px', overflow: 'visible' }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center border-b border-slate-800/50 px-3">
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="/intugle-icon.svg" 
              alt="Intugle" 
              className="flex-shrink-0"
              style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
            />
            <span 
              className={`text-white font-medium text-[17px] tracking-tight transition-all duration-300 overflow-hidden whitespace-nowrap ${
                isExpanded ? 'opacity-100 max-w-[150px] ml-[-8px]' : 'opacity-0 max-w-0 ml-0'
              }`}
              style={{ 
                fontFamily: 'system-ui, -apple-system, sans-serif',
                marginTop: '-1px'
              }}
            >
              Intugle
            </span>
          </div>
        </div>

        {/* Workspace Selector */}
        <div className="px-3 py-3 border-b border-slate-800/50">
          <div className="relative">
            <button
              onClick={() => setShowWorkspaceDropdown(!showWorkspaceDropdown)}
              className={`flex items-center gap-2 rounded-lg bg-slate-800/40 hover:bg-slate-800/70 transition-all duration-200 ${
                isExpanded ? 'w-full px-2.5 py-2' : 'w-10 h-10 justify-center'
              }`}
              title={!isExpanded ? currentWorkspace.name : undefined}
            >
              <currentWorkspace.icon className={`w-5 h-5 ${currentWorkspace.color} flex-shrink-0`} />
              {isExpanded && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-white truncate">{currentWorkspace.name}</p>
                    <p className="text-[10px] text-gray-500">{currentWorkspace.tables} tables</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${showWorkspaceDropdown ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            {/* Workspace Dropdown */}
            {showWorkspaceDropdown && (
              <div className={`absolute top-full left-0 mt-1 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-50 overflow-hidden ${
                isExpanded ? 'w-full' : 'w-56 left-0'
              }`}>
                <div className="px-3 py-2 border-b border-slate-700/50">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Your Workspaces</p>
                </div>
                <div className="py-1 max-h-64 overflow-y-auto">
                  {workspaces.map((workspace) => {
                    const isSelected = selectedWorkspace === workspace.id;
                    return (
                      <button
                        key={workspace.id}
                        onClick={() => {
                          setSelectedWorkspace(workspace.id);
                          setShowWorkspaceDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-800 transition-colors ${
                          isSelected ? 'bg-slate-800/50' : ''
                        }`}
                      >
                        <workspace.icon className={`w-5 h-5 ${workspace.color} flex-shrink-0`} />
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-sm font-medium text-white truncate">{workspace.name}</p>
                          <p className="text-[10px] text-gray-500">{workspace.tables} tables</p>
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-slate-700/50 p-2">
                  <button
                    onClick={() => {
                      setShowWorkspaceDropdown(false);
                      // TODO: Open create workspace modal
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors text-blue-400"
                  >
                    <div className="w-7 h-7 rounded-md bg-blue-500/10 flex items-center justify-center">
                      <Plus className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium">Create Workspace</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <div className="flex-1 py-4" style={{ overflow: isExpanded ? 'auto' : 'visible' }}>
          <nav className="space-y-2 px-3">
            {navItems.map((item, index) => (
              <div key={item.id} className="relative">
                <button
                  onClick={() => handleNavClick(item)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200
                    ${item.active 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-gray-400 hover:bg-slate-800/50 hover:text-white'}
                  `}
                  title={!isExpanded ? item.label : undefined}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`text-sm font-medium flex-1 text-left transition-all duration-300 overflow-hidden whitespace-nowrap ${
                    isExpanded ? 'opacity-100 max-w-[150px]' : 'opacity-0 max-w-0'
                  }`}>{item.label}</span>
                  {item.hasSubmenu && (
                    <ChevronRight className={`w-4 h-4 transition-all duration-300 ${sessionsExpanded ? 'rotate-90' : ''} ${
                      isExpanded ? 'opacity-100' : 'opacity-0 w-0'
                    }`} />
                  )}
                </button>

                {/* New Session Button - After Data Connections */}
                {item.id === 'connections' && (
                  <button
                    onClick={() => navigate('/app/session')}
                    className="w-full flex items-center gap-3 px-3 py-3 mt-2 rounded-lg bg-slate-800/30 hover:bg-slate-800/60 text-gray-400 hover:text-white transition-all duration-200"
                    title={!isExpanded ? 'New Session' : undefined}
                  >
                    <Plus className="w-5 h-5 flex-shrink-0 text-blue-500" />
                    <span className={`text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap ${
                      isExpanded ? 'opacity-100 max-w-[150px]' : 'opacity-0 max-w-0'
                    }`}>New Session</span>
                  </button>
                )}

                {/* Sessions Dropdown - For Collapsed State */}
                {item.id === 'session' && !isExpanded && showSessionsDropdown && (
                  <div 
                    ref={sessionsDropdownRef}
                    className="absolute left-full top-0 ml-2 w-56 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-50 overflow-hidden"
                  >
                    {/* Recent Sessions */}
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        <Clock className="w-3 h-3" />
                        <span>Recent</span>
                      </div>
                    </div>
                    <div className="py-1 max-h-40 overflow-y-auto">
                      {recentSessions.map((session) => (
                        <button
                          key={session.id}
                          onClick={() => {
                            setActiveSessionId(session.id);
                            setShowSessionsDropdown(false);
                            navigate('/app/session');
                          }}
                          className={`group w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-800 transition-colors ${
                            activeSessionId === session.id ? 'bg-slate-800/50 text-blue-400' : 'text-gray-300'
                          }`}
                        >
                          <span className="text-sm truncate">{session.name}</span>
                          <Star 
                            className={`w-3 h-3 flex-shrink-0 transition-all ${
                              starredSessionIds.includes(session.id)
                                ? 'text-yellow-500 fill-yellow-500 opacity-100'
                                : 'opacity-0 group-hover:opacity-100 text-gray-500 hover:text-yellow-500'
                            }`}
                            onClick={(e) => toggleStar(session.id, e)}
                          />
                        </button>
                      ))}
                    </div>
                    
                    {/* Starred Sessions */}
                    <div className="px-3 py-2 border-t border-slate-700/50">
                      <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        <Star className="w-3 h-3" />
                        <span>Starred</span>
                      </div>
                    </div>
                    <div className="py-1 max-h-40 overflow-y-auto">
                      {starredSessions.filter(s => starredSessionIds.includes(s.id)).map((session) => (
                        <button
                          key={session.id}
                          onClick={() => {
                            setActiveSessionId(session.id);
                            setShowSessionsDropdown(false);
                            navigate('/app/session');
                          }}
                          className={`group w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-800 transition-colors ${
                            activeSessionId === session.id ? 'bg-slate-800/50 text-blue-400' : 'text-gray-300'
                          }`}
                        >
                          <span className="text-sm truncate">{session.name}</span>
                          <Star 
                            className="w-3 h-3 flex-shrink-0 text-yellow-500 fill-yellow-500 hover:opacity-70 transition-opacity"
                            onClick={(e) => toggleStar(session.id, e)}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sessions Submenu */}
                {item.id === 'session' && isExpanded && sessionsExpanded && (
                  <div className="mt-1 ml-4 pl-4 border-l border-slate-700/50 space-y-1 max-h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                    {/* Recent Sessions */}
                    <div className="pt-1">
                      <div className="flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-wider text-gray-600 sticky top-0 bg-slate-950">
                        <Clock className="w-3 h-3" />
                        <span>Recent</span>
                      </div>
                      {recentSessions.map((session) => (
                        <button
                          key={session.id}
                          className={`group w-full flex items-center justify-between gap-2 px-3 py-1.5 transition-colors text-xs relative ${
                            activeSessionId === session.id
                              ? 'text-blue-400'
                              : 'text-gray-400 hover:bg-slate-800/50 hover:text-white rounded-lg'
                          }`}
                          onClick={() => {
                            setActiveSessionId(session.id);
                            navigate('/app/session');
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {activeSessionId === session.id && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-blue-500"></div>
                            )}
                            <span className="truncate">{session.name}</span>
                          </div>
                          <Star 
                            className={`w-3 h-3 flex-shrink-0 transition-all ${
                              starredSessionIds.includes(session.id)
                                ? 'text-yellow-500 fill-yellow-500 opacity-100'
                                : 'opacity-0 group-hover:opacity-100 text-gray-500 hover:text-yellow-500'
                            }`}
                            onClick={(e) => toggleStar(session.id, e)}
                          />
                        </button>
                      ))}
                    </div>

                    {/* Starred Sessions */}
                    <div className="pt-2">
                      <div className="flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-wider text-gray-600 sticky top-0 bg-slate-950">
                        <Star className="w-3 h-3" />
                        <span>Starred</span>
                      </div>
                      {starredSessions.filter(s => starredSessionIds.includes(s.id)).map((session) => (
                        <button
                          key={session.id}
                          className={`group w-full flex items-center justify-between gap-2 px-3 py-1.5 transition-colors text-xs relative ${
                            activeSessionId === session.id
                              ? 'text-blue-400'
                              : 'text-gray-400 hover:bg-slate-800/50 hover:text-white rounded-lg'
                          }`}
                          onClick={() => {
                            setActiveSessionId(session.id);
                            navigate('/app/session');
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {activeSessionId === session.id && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-blue-500"></div>
                            )}
                            <span className="truncate">{session.name}</span>
                          </div>
                          <Star 
                            className="w-3 h-3 flex-shrink-0 text-yellow-500 fill-yellow-500 opacity-100 hover:opacity-70 transition-opacity"
                            onClick={(e) => toggleStar(session.id, e)}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Bottom Navigation */}
        <div className="border-t border-slate-800/50 py-5 px-3 space-y-2">
          {/* Credits - Custom styling */}
          <button
            onClick={() => navigate('/app/credits')}
            className={`
              w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200
              ${location.pathname === '/app/credits'
                ? 'bg-blue-600/20 text-blue-400' 
                : 'bg-slate-800/30 hover:bg-slate-800/60 text-gray-300 hover:text-white'}
            `}
            title={`Credits: ${formatCredits(creditsBalance)}`}
          >
            {isExpanded ? (
              <div className="flex items-center gap-3 w-full">
                <CreditCard className="w-5 h-5 flex-shrink-0 text-blue-500" />
                <span className="text-sm font-medium flex-1 text-left">Credits</span>
                <span className="text-sm font-semibold text-blue-400">{formatCredits(creditsBalance)}</span>
              </div>
            ) : (
              <span className="text-sm font-semibold text-blue-400">{formatCredits(creditsBalance, true)}</span>
            )}
          </button>

          {/* Profile */}
          <button
            onClick={() => navigate('/app/profile')}
            className={`
              w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200
              ${location.pathname === '/app/profile'
                ? 'bg-blue-600/20 text-blue-400' 
                : 'text-gray-400 hover:bg-slate-800/50 hover:text-white'}
            `}
            title="Profile"
          >
            <User className="w-5 h-5 flex-shrink-0" />
            <span className={`text-sm font-medium flex-1 text-left transition-all duration-300 overflow-hidden whitespace-nowrap ${
              isExpanded ? 'opacity-100 max-w-[150px]' : 'opacity-0 max-w-0'
            }`}>Profile</span>
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 text-gray-500 hover:bg-slate-800/50 hover:text-white"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronLeft className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${!isExpanded ? 'rotate-180' : ''}`} />
            <span className={`text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap ${
              isExpanded ? 'opacity-100 max-w-[150px]' : 'opacity-0 max-w-0'
            }`}>Collapse</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
};

export default AppLayout;
